// src/components/admin/ReviewApplicationModal.tsx

import React, { useState } from 'react';
import {
    X, Building2, User, Mail, Phone, Calendar, Globe,
    ExternalLink, CheckCircle, XCircle, AlertCircle,
    FileText, MapPin, Check, Clock
} from 'lucide-react';
import { formatPhoneNumber } from '../../utils/phoneFormatter';
import AddressMiniMapDisplay from './AddressMiniMapDisplay';
import DocumentViewer from './DocumentViewer';
import Select from '../common/Select';

interface ReviewApplicationModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: (action: 'approve' | 'reject', reason?: string) => Promise<void>;
    application: {
        id: number;
        business_name: string;
        business_email: string;
        business_phone: string;
        vendor_type: string;
        website?: string;
        description?: string;
        applicant_name?: string;
        verification_status: 'pending' | 'approved' | 'rejected';
        created_at: string;
        updated_at?: string;
        user: {
            first_name: string;
            last_name: string;
            email: string;
        };
        business_address?: any;
        registration_file?: string;
        address_proof?: string;
        rejection_reason?: string;
    };
    isProcessing?: boolean;
}

const REJECTION_REASONS = [
    {
        value: 'incomplete_info',
        label: 'Incomplete Information',
        description: 'Missing or incomplete business details, address, or contact information'
    },
    {
        value: 'business_type_mismatch',
        label: 'Business Type Mismatch',
        description: 'The selected business type does not match the provided documentation'
    },
    {
        value: 'document_issues',
        label: 'Document Issues',
        description: 'Documents are missing, unclear, or do not meet our requirements'
    },
    {
        value: 'policy_violation',
        label: 'Policy Violation',
        description: 'The application violates our platform policies or terms of service'
    },
    {
        value: 'duplicate_application',
        label: 'Duplicate Application',
        description: 'A similar application already exists for this business'
    },
    {
        value: 'other',
        label: 'Other',
        description: 'Please provide a detailed reason below'
    },
];

const StatusBadge = ({ status }: { status: string }) => {
    const config = {
        pending: { label: 'Pending', className: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
        approved: { label: 'Approved', className: 'bg-green-100 text-green-700 border-green-200' },
        rejected: { label: 'Rejected', className: 'bg-red-100 text-red-700 border-red-200' },
    };
    const { label, className } = config[status as keyof typeof config] || config.pending;
    return (
        <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium border ${className}`}>
            {status === 'pending' && <Clock className="h-4 w-4" />}
            {status === 'approved' && <CheckCircle className="h-4 w-4" />}
            {status === 'rejected' && <XCircle className="h-4 w-4" />}
            {label}
        </span>
    );
};

const InfoRow = ({ label, value, icon: Icon }: { label: string; value?: string | null; icon?: any }) => {
    if (!value) return null;
    return (
        <div className="flex items-start gap-3 py-2 border-b border-gray-100 last:border-0">
            {Icon && <Icon className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />}
            <div className="flex-1 min-w-0">
                <p className="text-xs text-gray-500 uppercase tracking-wider">{label}</p>
                <p className="text-sm text-gray-900 break-words">{value}</p>
            </div>
        </div>
    );
};

const ReviewApplicationModal: React.FC<ReviewApplicationModalProps> = ({
    isOpen,
    onClose,
    onConfirm,
    application,
    isProcessing = false,
}) => {
    const [action, setAction] = useState<'approve' | 'reject'>('approve');
    const [rejectionReason, setRejectionReason] = useState('');
    const [rejectionReasonCustom, setRejectionReasonCustom] = useState('');
    const [showRejectionErrors, setShowRejectionErrors] = useState(false);
    const [showApproveModal, setShowApproveModal] = useState(false);
    const [showRejectModal, setShowRejectModal] = useState(false);

    if (!isOpen) return null;

    const handleRejectClick = () => {
        if (!rejectionReason) {
            setShowRejectionErrors(true);
            return;
        }
        if (rejectionReason === 'other' && !rejectionReasonCustom.trim()) {
            setShowRejectionErrors(true);
            return;
        }
        setShowRejectModal(true);
    };

    const handleApprove = async () => {
        await onConfirm('approve');
        setShowApproveModal(false);
    };

    const handleReject = async () => {
        let reason;

        if (rejectionReason === 'other') {
            // For "other", use just the custom description
            reason = rejectionReasonCustom;
        } else {
            // For predefined reasons, combine label and description
            const selected = REJECTION_REASONS.find(r => r.value === rejectionReason);
            const label = selected?.label || '';
            const description = selected?.description || '';

            // Combine label and description with a colon or dash
            reason = `${label}:\n${description}`;
        }

        await onConfirm('reject', reason);
        setShowRejectModal(false);
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        });
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

            <div className="relative bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-blue-100 rounded-lg">
                            <Building2 className="h-5 w-5 text-blue-600" />
                        </div>
                        <div>
                            <h3 className="text-lg font-semibold text-gray-900">Review Application</h3>
                            <p className="text-sm text-gray-500">{application.business_name}</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <StatusBadge status={application.verification_status} />
                        <button
                            type="button"
                            onClick={onClose}
                            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                            <X className="h-5 w-5" />
                        </button>
                    </div>
                </div>

                {/* Body */}
                <div className="p-6">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        {/* Main Info */}
                        <div className="lg:col-span-2 space-y-6">
                            {/* Business Info */}
                            <div className="bg-gray-50 rounded-xl border border-gray-200 p-6">
                                <h4 className="font-semibold text-gray-900 mb-4">Business Information</h4>
                                <div className="space-y-2">
                                    <InfoRow label="Business Name" value={application.business_name} />
                                    <InfoRow label="Business Email" value={application.business_email} icon={Mail} />
                                    <InfoRow label="Phone" value={formatPhoneNumber(application.business_phone)} icon={Phone} />
                                    <InfoRow label="Business Type" value={application.vendor_type} />
                                    {application.website && (
                                        <div className="flex items-start gap-3 py-2 border-b border-gray-100 last:border-0">
                                            <Globe className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                                            <div className="flex-1 min-w-0">
                                                <p className="text-xs text-gray-500 uppercase tracking-wider">Website</p>
                                                <a
                                                    href={application.website.match(/^https?:\/\//) ? application.website : `https://${application.website}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-sm text-blue-600 hover:underline font-medium truncate inline-flex items-center gap-1"
                                                >
                                                    {application.website.replace(/^https?:\/\//, '')}
                                                    <ExternalLink className="h-3 w-3 flex-shrink-0" />
                                                </a>
                                            </div>
                                        </div>
                                    )}
                                    {application.description && <InfoRow label="Description" value={application.description} />}
                                </div>
                            </div>

                            {/* Address */}
                            {application.business_address && (
                                <AddressMiniMapDisplay address={application.business_address} />
                            )}

                            {/* Documents */}
                            {(application.registration_file || application.address_proof) && (
                                <div className="bg-gray-50 rounded-xl border border-gray-200 p-6">
                                    <h4 className="font-semibold text-gray-900 mb-4">Uploaded Documents</h4>
                                    <div className="space-y-3">
                                        {application.registration_file && (
                                            <DocumentViewer
                                                file={application.registration_file}
                                                label="Registration Certificate"
                                            />
                                        )}
                                        {application.address_proof && (
                                            <DocumentViewer
                                                file={application.address_proof}
                                                label="Proof of Address"
                                            />
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Sidebar - Actions */}
                        <div className="space-y-6">
                            {/* Status Card */}
                            <div className="bg-gray-50 rounded-xl border border-gray-200 p-6">
                                <h4 className="font-semibold text-gray-900 mb-4">Status</h4>
                                <div className="flex items-center gap-2 mb-4">
                                    <StatusBadge status={application.verification_status} />
                                </div>
                                <div className="text-sm text-gray-500 space-y-1">
                                    <p>Submitted: {formatDate(application.created_at)}</p>
                                    {application.updated_at && (
                                        <p>Last updated: {formatDate(application.updated_at)}</p>
                                    )}
                                </div>
                            </div>

                            {/* Actions */}
                            {application.verification_status === 'pending' && (
                                <div className="bg-gray-50 rounded-xl border border-gray-200 p-6">
                                    <h4 className="font-semibold text-gray-900 mb-4">Actions</h4>

                                    {/* Approve */}
                                    <button
                                        onClick={() => setShowApproveModal(true)}
                                        disabled={isProcessing}
                                        className="w-full mb-3 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                                    >
                                        <CheckCircle className="h-4 w-4" />
                                        Approve Application
                                    </button>
                                    {/* Reject */}
                                    <div className="border-t border-gray-200 pt-3">
                                        <p className="text-sm font-medium text-gray-700 mb-2">
                                            Rejection Reason <span className="text-red-500">*</span>
                                        </p>

                                        <Select
                                            value={rejectionReason}
                                            onChange={(value) => {
                                                setRejectionReason(value);
                                                setShowRejectionErrors(false);
                                                if (value !== 'other') {
                                                    const selected = REJECTION_REASONS.find(r => r.value === value);
                                                    setRejectionReasonCustom(selected?.description || '');
                                                } else {
                                                    setRejectionReasonCustom('');
                                                }
                                            }}
                                            options={REJECTION_REASONS.map(r => ({
                                                value: r.value,
                                                label: r.label,
                                                description: r.description,
                                            }))}
                                            placeholder="Select a reason..."
                                            error={showRejectionErrors && !rejectionReason ? 'Please select a reason' : undefined}
                                            required={true}
                                        />

                                        {rejectionReason && (
                                            <div className="mt-3">
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    {rejectionReason === 'other' ? 'Please specify the reason *' : 'Additional Details (optional)'}
                                                </label>
                                                <textarea
                                                    value={rejectionReasonCustom}
                                                    onChange={(e) => {
                                                        setRejectionReasonCustom(e.target.value);
                                                        setShowRejectionErrors(false);
                                                    }}
                                                    placeholder={rejectionReason === 'other'
                                                        ? 'Provide a detailed reason for rejection...'
                                                        : 'Add any additional context or notes...'
                                                    }
                                                    rows={3}
                                                    className={`w-full border rounded-lg p-2 text-sm focus:ring-2 focus:ring-red-500 resize-none ${showRejectionErrors && rejectionReason === 'other' && !rejectionReasonCustom.trim()
                                                        ? 'border-red-300 bg-red-50'
                                                        : 'border-gray-300'
                                                        }`}
                                                />
                                                {showRejectionErrors && rejectionReason === 'other' && !rejectionReasonCustom.trim() && (
                                                    <div className="mt-1 flex items-center text-sm text-red-600">
                                                        <AlertCircle className="h-4 w-4 mr-1 flex-shrink-0" />
                                                        Please provide a reason
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        <button
                                            onClick={handleRejectClick}
                                            disabled={isProcessing}
                                            className="w-full mt-3 px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                                        >
                                            <XCircle className="h-4 w-4" />
                                            Reject Application
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Already Reviewed */}
                            {application.verification_status !== 'pending' && (
                                <div className="bg-gray-50 rounded-xl border border-gray-200 p-6 text-center">
                                    <p className="text-sm text-gray-500 flex items-center justify-center gap-2">
                                        {application.verification_status === 'approved' ? (
                                            <>
                                                <CheckCircle className="h-6 w-6 text-green-500" />
                                                This application has been approved
                                            </>
                                        ) : (
                                            <>
                                                <XCircle className="h-4 w-4 text-red-500" />
                                                This application has been rejected
                                            </>
                                        )}
                                    </p>
                                    {application.rejection_reason && (
                                        <p className="text-sm text-red-600 mt-2">
                                            Reason: {application.rejection_reason}
                                        </p>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Approve Confirmation Modal */}
                {showApproveModal && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
                        <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowApproveModal(false)} />
                        <div className="relative bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">Approve Application?</h3>
                            <p className="text-sm text-gray-600 mb-6">
                                Are you sure you want to approve "{application.business_name}" as a vendor? They will gain immediate access to the vendor dashboard.
                            </p>
                            <div className="flex justify-end gap-3">
                                <button
                                    onClick={() => setShowApproveModal(false)}
                                    className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleApprove}
                                    disabled={isProcessing}
                                    className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50"
                                >
                                    {isProcessing ? 'Processing...' : 'Approve'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Reject Confirmation Modal */}
                {showRejectModal && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
                        <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowRejectModal(false)} />
                        <div className="relative bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">Reject Application?</h3>
                            <p className="text-sm text-gray-600 mb-4">
                                Are you sure you want to reject "{application.business_name}"?
                            </p>
                            <p className="text-sm text-red-600 mb-6">
                                <strong>Reason:</strong> {rejectionReason === 'other' ? rejectionReasonCustom : REJECTION_REASONS.find(r => r.value === rejectionReason)?.label}
                            </p>
                            <div className="flex justify-end gap-3">
                                <button
                                    onClick={() => setShowRejectModal(false)}
                                    className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleReject}
                                    disabled={isProcessing}
                                    className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50"
                                >
                                    {isProcessing ? 'Processing...' : 'Reject'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ReviewApplicationModal;